import type { CampaignKind } from '#/shared/api/generated/communication/models'

/**
 * Campaign vocabulary shared by the index, the detail tabs and the enrolment
 * modals.
 *
 * A campaign is a *sequence*: an audience joins it, walks a list of steps
 * (SMS or email, each with its own delay and guards) and leaves through an exit
 * rule. Everything the UI shows about one is a rollup the backend computes —
 * the row below is already-formatted display data, never numbers the client
 * derives.
 */

export const CAMPAIGN_STATUSES = {
  running: 'running',
  scheduled: 'scheduled',
  finished: 'finished',
  paused: 'paused',
  stopped: 'stopped',
  draft: 'draft',
} as const

export type CampaignStatus =
  (typeof CAMPAIGN_STATUSES)[keyof typeof CAMPAIGN_STATUSES]

export const CAMPAIGN_CHANNELS = {
  sms: 'sms',
  email: 'email',
  /**
   * Agent work, not a message. A task step walks the sequence like any other —
   * same delay, same `only_if` — but reaches somebody the sequence cannot
   * otherwise contact: it needs no email or phone, spends no frequency cap
   * (the cap protects a customer from being written to, not an agent from
   * being given work) and ignores the send window, because a task queued at
   * 3am is read at nine. Its title travels in `subject`, where an email step
   * puts its subject line.
   */
  task: 'task',
} as const

export type CampaignChannel =
  (typeof CAMPAIGN_CHANNELS)[keyof typeof CAMPAIGN_CHANNELS]

/**
 * Which accent fills an owner's avatar. The design draws owners from the same
 * three inks as the status pills rather than hashing the name to a hue, so the
 * choice travels with the row.
 */
export const CAMPAIGN_OWNER_ACCENTS = {
  blue: 'blue',
  green: 'green',
  amber: 'amber',
} as const

export type CampaignOwnerAccent =
  (typeof CAMPAIGN_OWNER_ACCENTS)[keyof typeof CAMPAIGN_OWNER_ACCENTS]

/** A figure that reads as a primary number with a quieter line beneath it. */
export interface CampaignMetric {
  /** Already formatted; `null` renders the em-dash placeholder. */
  value: string | null
  /** Secondary line under the value. Omit when the row has nothing to add. */
  note?: string
}

export interface CampaignOwner {
  name: string
  accent: CampaignOwnerAccent
  avatarUrl?: string
}

export interface CampaignRow {
  id: string
  name: string
  /** How someone already in behaves: `sequence` once per person, `automation`
   *  once per event, `broadcast` one shot. */
  kind: CampaignKind
  channels: CampaignChannel[]
  /** "Manual · 141 by Ray T. · New, Lead vendor" — how the audience is built. */
  audience: string
  status: CampaignStatus
  /** Enrolled count plus its send-progress note. */
  people: CampaignMetric
  /**
   * Unanswered inbound replies and how old the oldest one is ("4 · 22 min").
   * `null` when the campaign has never sent; `'0'` when nothing is waiting.
   */
  waitingOnUs: string | null
  /** True while replies are actually waiting — drives the attention colour. */
  waitingOverdue: boolean
  replies: string | null
  booked: CampaignMetric
  margin: CampaignMetric
  owner: CampaignOwner
  /** "Today 2:41 PM", "Starts Aug 4", or `null` for a draft. */
  lastSend: string | null
}
